import { Skeleton, useTheme } from '@mui/material';

import { Sparkline } from '../sparkline/Sparkline';

import { KpiTileProps } from './KpiTile.types';
import * as Styled from './KpiTile.styles';

const percentChange = (current: number, previous: number): number | undefined => {
  if (previous === 0) {
    return current === 0 ? 0 : undefined;
  }

  return ((current - previous) / Math.abs(previous)) * 100;
};

/**
 * One KPI: label, big value, change against the previous window, and a trend line.
 *
 * `goodDirection` decides whether an arrow is green or red, because "up" is good for
 * delivered/min and bad for dead letter. The arrow glyph carries the direction, the colour only
 * carries the judgement, so the tile still reads without colour.
 */
export const KpiTile = ({
  label,
  value,
  delta,
  deltaText,
  goodDirection = 'neutral',
  sparkline = [],
  tone = 'default',
  caption,
  isLoading = false,
  onClick,
}: KpiTileProps): JSX.Element => {
  const { palette } = useTheme();

  const change = delta ? percentChange(delta.current, delta.previous) : undefined;
  const isUp = delta ? delta.current > delta.previous : false;
  const isFlat = delta ? delta.current === delta.previous : true;

  const deltaTone: 'good' | 'bad' | 'neutral' =
    !delta || isFlat || goodDirection === 'neutral'
      ? 'neutral'
      : (isUp ? 'up' : 'down') === goodDirection
      ? 'good'
      : 'bad';

  const arrow = isFlat ? '' : isUp ? '▲' : '▼';
  const computedDeltaText =
    deltaText ?? (change === undefined ? undefined : `${arrow} ${Math.abs(Math.round(change))} %`.trim());

  const sparklineColor =
    tone === 'ok'
      ? palette.chart.ok
      : tone === 'warn'
      ? palette.chart.warn
      : tone === 'bad'
      ? palette.chart.bad
      : undefined;

  return (
    <Styled.Tile $isClickable={!!onClick} onClick={onClick} data-testid="dod-kpi-tile">
      <Styled.Label>{label}</Styled.Label>

      {isLoading ? (
        <Skeleton variant="text" width="60%" height={38} />
      ) : (
        <Styled.ValueRow>
          <Styled.Value $tone={tone}>{value}</Styled.Value>
          {computedDeltaText && (
            <Styled.Delta $tone={deltaTone} data-testid="dod-kpi-delta">
              {computedDeltaText}
            </Styled.Delta>
          )}
        </Styled.ValueRow>
      )}

      {caption && <Styled.Caption>{caption}</Styled.Caption>}
      {!isLoading && !caption && <Sparkline data={sparkline} color={sparklineColor} />}
    </Styled.Tile>
  );
};
